import { useMemo, useRef, useState } from 'react'
import type { Person } from '../../types'
import { usePeople } from '../../hooks/usePeople'
import { inputClass } from '../modal-form'

// PersonPicker + PersonMultiPicker — shared combobox primitives for spec 15
// §3, spec 17 §3, etc. Both consume `usePeople()`.
//
// Filter prop is a callback rather than a pre-filtered pool so consumers
// can express e.g. "role === 'Welcome Host' AND not already assigned today"
// without the parent having to recompute the list on every change. The
// hook caches the people list at the query layer; the filter runs at
// render time on a stable array.
//
// Empty-state distinguishes "no people in this pool at all" (filter rules
// out everyone) from "your search returned nothing" (live typeahead empty).

type CommonProps = {
  filter?: (p: Person) => boolean
  placeholder?: string
  disabled?: boolean
}

type SingleProps = CommonProps & {
  value: string | null
  onChange: (id: string | null) => void
}

type MultiProps = CommonProps & {
  value: string[]
  onChange: (ids: string[]) => void
}

// Shared search-and-pick combobox panel. Single uses it as the input; Multi
// hangs it below the selected-chip list.
function ComboboxList({
  pool,
  filterText,
  onPick,
  hideIds
}: {
  pool: Person[]
  filterText: string
  onPick: (p: Person) => void
  hideIds?: Set<string>
}) {
  const lowered = filterText.toLowerCase().trim()
  const filtered = useMemo(() => {
    return pool.filter((p) => {
      if (hideIds?.has(p.id)) return false
      if (!lowered) return true
      return (
        p.name.toLowerCase().includes(lowered) ||
        p.role.toLowerCase().includes(lowered) ||
        p.dept.toLowerCase().includes(lowered)
      )
    })
  }, [pool, lowered, hideIds])

  if (filtered.length === 0) {
    // Distinguish "empty pool" vs "filtered out by search".
    const reason =
      pool.length === 0 || (hideIds && pool.every((p) => hideIds.has(p.id)))
        ? 'No one available.'
        : `No matches for "${filterText}".`
    return (
      <div className="p-3 text-sm text-ink-soft italic">{reason}</div>
    )
  }

  return (
    <ul role="listbox" className="max-h-64 overflow-y-auto">
      {filtered.map((p) => (
        <li key={p.id} role="option" aria-selected="false">
          <button
            type="button"
            onClick={() => onPick(p)}
            className="w-full text-left px-3 py-2 hover:bg-parchment flex items-center gap-2"
          >
            <span className="inline-flex items-center justify-center w-7 h-7 rounded-full bg-green/10 text-green text-xs font-semibold">
              {p.avatarInitials}
            </span>
            <span className="flex-1">
              <div className="text-sm">{p.name}</div>
              <div className="text-xs text-ink-soft">
                {p.role} · {p.dept}
              </div>
            </span>
          </button>
        </li>
      ))}
    </ul>
  )
}

export function PersonPicker({
  value,
  onChange,
  filter,
  placeholder = 'Search…',
  disabled
}: SingleProps) {
  const { data: people = [], isLoading } = usePeople()
  const [open, setOpen] = useState(false)
  const [search, setSearch] = useState('')
  const inputRef = useRef<HTMLInputElement>(null)

  const pool = useMemo(
    () => (filter ? people.filter(filter) : people),
    [people, filter]
  )
  const selected = value ? people.find((p) => p.id === value) ?? null : null

  function clear() {
    onChange(null)
    setSearch('')
    inputRef.current?.focus()
  }

  function pick(p: Person) {
    onChange(p.id)
    setSearch('')
    setOpen(false)
  }

  if (selected && !open) {
    return (
      <div
        className={`${inputClass(false)} flex items-center gap-2 cursor-pointer`}
        onClick={() => !disabled && setOpen(true)}
        role="button"
        tabIndex={0}
      >
        <span className="inline-flex items-center justify-center w-7 h-7 rounded-full bg-green/10 text-green text-xs font-semibold">
          {selected.avatarInitials}
        </span>
        <span className="flex-1 text-sm">
          {selected.name}
          <span className="text-xs text-ink-soft ml-2">
            {selected.role}
          </span>
        </span>
        {!disabled && (
          <button
            type="button"
            onClick={(e) => {
              e.stopPropagation()
              clear()
            }}
            aria-label="Clear"
            className="text-ink-soft hover:text-oxblood text-lg leading-none w-6 h-6 flex items-center justify-center rounded hover:bg-parchment"
          >
            ×
          </button>
        )}
      </div>
    )
  }

  return (
    <div className="relative">
      <input
        ref={inputRef}
        type="text"
        value={search}
        onChange={(e) => {
          setSearch(e.target.value)
          setOpen(true)
        }}
        onFocus={() => setOpen(true)}
        onBlur={() => {
          // Defer close so a click on the list resolves first.
          setTimeout(() => setOpen(false), 150)
        }}
        placeholder={isLoading ? 'Loading…' : placeholder}
        disabled={disabled || isLoading}
        className={inputClass(false)}
      />
      {open && !isLoading && (
        <div className="absolute z-10 mt-1 w-full bg-white border border-line rounded shadow-lg">
          <ComboboxList pool={pool} filterText={search} onPick={pick} />
        </div>
      )}
    </div>
  )
}

export function PersonMultiPicker({
  value,
  onChange,
  filter,
  placeholder = 'Search…',
  disabled
}: MultiProps) {
  const { data: people = [], isLoading } = usePeople()
  const [search, setSearch] = useState('')
  const [open, setOpen] = useState(false)

  const pool = useMemo(
    () => (filter ? people.filter(filter) : people),
    [people, filter]
  )
  const selectedIds = useMemo(() => new Set(value), [value])
  const selected = useMemo(
    () => value.map((id) => people.find((p) => p.id === id)).filter(Boolean) as Person[],
    [value, people]
  )

  function remove(id: string) {
    onChange(value.filter((v) => v !== id))
  }

  function pick(p: Person) {
    if (selectedIds.has(p.id)) return
    onChange([...value, p.id])
    setSearch('')
    // Stay open — the manager probably wants to add multiple.
  }

  return (
    <div>
      {selected.length > 0 && (
        <div className="flex flex-wrap gap-1.5 mb-2">
          {selected.map((p) => (
            <span
              key={p.id}
              className="inline-flex items-center gap-1.5 pl-2 pr-1 py-1 rounded-full bg-green/10 text-green text-xs"
            >
              <span className="font-semibold">{p.avatarInitials}</span>
              <span>{p.name}</span>
              {!disabled && (
                <button
                  type="button"
                  onClick={() => remove(p.id)}
                  aria-label={`Remove ${p.name}`}
                  className="hover:bg-green/20 rounded-full w-4 h-4 flex items-center justify-center"
                >
                  ×
                </button>
              )}
            </span>
          ))}
        </div>
      )}
      <div className="relative">
        <input
          type="text"
          value={search}
          onChange={(e) => {
            setSearch(e.target.value)
            setOpen(true)
          }}
          onFocus={() => setOpen(true)}
          onBlur={() => setTimeout(() => setOpen(false), 150)}
          placeholder={isLoading ? 'Loading…' : placeholder}
          disabled={disabled || isLoading}
          className={inputClass(false)}
        />
        {open && !isLoading && (
          <div className="absolute z-10 mt-1 w-full bg-white border border-line rounded shadow-lg">
            <ComboboxList
              pool={pool}
              filterText={search}
              onPick={pick}
              hideIds={selectedIds}
            />
          </div>
        )}
      </div>
    </div>
  )
}
